import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { colors } from "app/popleads/components/ui-kit/colors";
import { useDropzone } from "react-dropzone";
import { ExtensionBody, ExtensionSmall } from "app/popleads/components/ui-kit/Text";
import { toast } from "react-toastify";
import { FileDetails } from "app/popleads/modules/extension/components/NotesFileCard";
import { toAbsoluteUrl } from "_metronic/_helpers";
import { decideFileType } from "app/popleads/modules/activities/utils";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import Spinner from "app/popleads/components/ui-kit/Spinner";
import SVG from "react-inlinesvg";
import Modal from "app/popleads/components/ui-kit/Modal";
import { post } from "app/popleads/networking/RequestService";
import { useExtensionContext } from "../ExtensionContext";
import useUploadDealAttachments from "app/popleads/hooks/useUploadDealAttachments";
import MaterialIcon from "app/popleads/components/ui-kit/MaterialIcon";

const Container = styled.div`
    position: relative;
`;

const LoadingOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1;
    background: rgba(248, 248, 252, 0.7);
`;

const StyledDragAndDrop = styled.div`
    padding: 8px 8px;
    font-size: 14px;
    background-color: ${colors.neutral[25]};
    border-width: 2px;
    border-style: dashed;
    border-color: ${({ isDragActive, isDisabled }) =>
        isDragActive && !isDisabled ? colors.l3rnTech[900] : colors.neutral[300]};
    border-radius: 8px;
    display: flex;
    align-items: center;
    gap: 8px;
    color: ${({ isDisabled }) => (isDisabled ? colors.neutral[300] : colors.neutral[900])};
`;

const Wrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-bottom: 4px;
`;

const FileItem = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 6px;
    background-color: ${colors.l3rnTech[50]};
    border-radius: 8px;
    max-width: 30px;
    overflow: hidden;
    transition: max-width 0.2s ease-in-out;
    &:hover {
        max-width: 80px;
    }
    & > span {
        color: ${colors.neutral[500]};
        font-size: 12px;
        font-weight: 400;
        line-height: 20px;
    }
`;

const StyledTooltip = styled(Tooltip)`
    .tooltip-inner {
        font-size: 11px !important;
    }
`;

const StyledText = styled(ExtensionBody)`
    font-size: ${({ size }) => (size === "sm" ? "12px" : "14px")};
`;

const MAX_SIZE_BYTES = 1024 * 1024; // 1 MB

const GENERIC_ACCEPT = {
    "image/jpeg": [".jpg", ".jpeg"],
    "image/png": [".png"],
    "application/pdf": [".pdf"],
    "application/msword": [".doc"],
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
    "application/vnd.ms-excel": [".xls"],
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
    "text/plain": [".txt"],
    "video/mp4": [".mp4"],
    "text/csv": [".csv"],
    "image/svg+xml": [".svg"],
    "application/zip": [".zip"],
    "application/x-rar-compressed": [".rar"],
};

// CIPD's CV upload is a dedicated, stricter slot: only PDF and DOCX are
// accepted, unlike the generic "Upload Document" attachments above.
const CV_ACCEPT = {
    "application/pdf": [".pdf"],
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
};

function ExtensionDealAttachments({
    maxFiles,
    existingFiles = [],
    size = "md",
    isDisabled = false,
    dealId,
    onFilesChange,
    isCvAttachment = false,
    onCvDeleted,
}) {
    const { dealsLoading, refetchDeals, refetchAttachments, setDealAttachments } = useExtensionContext();
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [fileId, setFileId] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const { uploadDealAttachments } = useUploadDealAttachments({
        onSuccess: async () => {
            await refetchDeals();
            await refetchAttachments();
            toast.success("Files uploaded successfully");
            setIsLoading(false);
            setSelectedFiles([]);
        },
        onError: (error) => {
            toast.error(error || "Failed to upload attachments");
            setIsLoading(false);
            setSelectedFiles([]);
        },
    });

    const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
        disabled: isDisabled,
        noClick: true,
        noKeyboard: true,
        maxSize: MAX_SIZE_BYTES * 50, // 50 MB
        accept: isCvAttachment ? CV_ACCEPT : GENERIC_ACCEPT,
        onDrop: (acceptedFiles, rejectedFiles) => {
            if (rejectedFiles.length > 0) {
                const maxSizeError = rejectedFiles.some((file) =>
                    file.errors.some((error) => error.code === "file-too-large")
                );
                if (maxSizeError) {
                    toast.error("Your file exceeds limit. Maximum file size is 50MB.");
                } else if (isCvAttachment) {
                    toast.error("Please upload only PDF or DOCX files");
                } else {
                    toast.error("Please upload only DOC, XLS, TXT, PDF, CSV, ZIP, RAR, MP4, JPEG, PNG, or SVG files");
                }
                return;
            }
            if (acceptedFiles.length === 0) return;
            const totalFiles = existingFiles?.length + acceptedFiles.length;
            const totalSelectedFiles = selectedFiles.length + acceptedFiles.length;
            if (totalFiles > maxFiles || totalSelectedFiles > maxFiles) {
                toast.error(`You are able to upload max ${maxFiles} documents`);
                return;
            }
            setSelectedFiles((prevFiles) =>
                [...prevFiles, ...acceptedFiles].map((file) =>
                    Object.assign(file, {
                        preview: URL.createObjectURL(file),
                    })
                )
            );
        },
    });

    // The generic "Upload Document" attachments (profile.attachments /
    // selectedDeal.attachmentList) always carry a `downloadLink`, but the CIPD CV
    // slot's attachment comes from a different endpoint (getDealById's
    // `cvAttachment`) whose backend shape has varied - fall back across the
    // link field names it has actually been seen using instead of silently
    // no-op'ing when `downloadLink` isn't present.
    const getFileDownloadLink = (file) =>
        file?.downloadLink || file?.url || file?.fileUrl || file?.link || file?.path || file?.attachmentUrl;

    const handleDownload = (downloadLink, filename) => {
        if (!downloadLink) {
            toast.error("This file has no download link available.");
            return;
        }

        const anchorElement = document.createElement("a");
        anchorElement.href = downloadLink;
        anchorElement.download = filename || "download";
        anchorElement.target = "_blank";
        document.body.appendChild(anchorElement);
        anchorElement.click();
        document.body.removeChild(anchorElement);
    };

    const handleDeleteAttachment = async (fileId) => {
        if (!fileId) return;
        try {
            const deleteObj = {
                fn: "deleteAttachment",
                attachmentId: fileId,
            };
            setIsLoading(true);
            const response = await post(deleteObj);

            if (response.status === 200) {
                refetchDeals();
                refetchAttachments();
                toast.success("Attachment deleted.");
            } else {
                toast.error("Failed to delete file");
            }
        } catch (error) {
            const errorMessage = error?.response?.data?.text || error?.message || "Failed to delete file";
            toast.error(errorMessage);
        } finally {
            setIsLoading(false);
            setFileId(null);
        }
    };

    // The CV is a mandatory deal document, so deleting it can't go through the generic
    // deleteAttachment fn (the backend rejects that - it would leave deal.cv_attachment_id
    // dangling). deleteDealCv is the sanctioned counterpart: it clears cv_attachment_id and
    // removes the file in one backend call, keyed by dealId rather than attachmentId.
    const handleDeleteCv = async () => {
        if (!dealId) return;
        try {
            setIsLoading(true);
            const response = await post({ fn: "deleteDealCv", dealId });

            if (response.status === 200) {
                // refetchDeals/refetchAttachments only refresh the context's deal list, which
                // the edit screen merges with a `||` that never overwrites a truthy value with
                // the new falsy one (see ExtensionEditDeal.js) - so the CV would still show
                // until the screen was closed and reopened. Clear it in the caller's own state
                // right away instead of waiting on that round trip.
                if (onCvDeleted) onCvDeleted();
                await refetchDeals();
                await refetchAttachments();
                toast.success("CV deleted.");
            } else {
                toast.error("Failed to delete CV");
            }
        } catch (error) {
            const errorMessage = error?.response?.data?.text || error?.message || "Failed to delete CV";
            toast.error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        // The CV slot never auto-uploads through uploadDealAttachments (the generic-document
        // endpoint) even once a dealId exists - it's only used here to target the delete
        // affordance below. CV files are handed back via onFilesChange and uploaded through
        // uploadDealCv at deal-save time instead (see ExtensionContext.js).
        if (isCvAttachment) {
            (onFilesChange || setDealAttachments)(selectedFiles);
            return;
        }
        if (selectedFiles?.length > 0 && dealId) {
            setIsLoading(true);
            uploadDealAttachments({ files: selectedFiles, dealId });
        } else if (!dealId) {
            (onFilesChange || setDealAttachments)(selectedFiles);
        }
    }, [selectedFiles, dealId, isCvAttachment]);

    return (
        <Container>
            {isLoading || dealsLoading ? (
                <LoadingOverlay>
                    <Spinner size={32} thickness={4} variant="l3rnTech" />
                </LoadingOverlay>
            ) : null}
            {selectedFiles?.length > 0 || existingFiles?.length > 0 ? (
                <Wrapper>
                    {existingFiles?.map((file, index) => (
                        <FileItem key={index}>
                            <FileDetails>
                                <OverlayTrigger
                                    placement="top"
                                    overlay={<StyledTooltip id={`tooltip-${index}`}>{file.filename}</StyledTooltip>}
                                >
                                    <span>
                                        <SVG src={toAbsoluteUrl(decideFileType(file?.filename))} />
                                    </span>
                                </OverlayTrigger>
                            </FileDetails>
                            <MaterialIcon
                                icon="arrow_circle_down"
                                style={{ fontSize: "20px", cursor: "pointer" }}
                                onClick={() => handleDownload(getFileDownloadLink(file), file.filename)}
                            />
                            {/* The CV is a mandatory deal document - the backend's
                                deleteAttachment rejects removing it standalone since that
                                would leave deal.cv_attachment_id dangling. deleteDealCv is
                                the dedicated fn that clears cv_attachment_id and the file
                                together, keyed by dealId rather than attachmentId - it
                                needs a saved deal to target, so the affordance only shows
                                once dealId exists (i.e. not while still creating the deal). */}
                            {(!isCvAttachment || dealId) && (
                                <MaterialIcon
                                    icon="close"
                                    style={{ fontSize: "20px", cursor: "pointer" }}
                                    onClick={() => {
                                        setModalOpen(true);
                                        setFileId(file.id);
                                    }}
                                />
                            )}
                        </FileItem>
                    ))}
                    {selectedFiles?.map((file, index) => (
                        <FileItem key={index}>
                            <FileDetails>
                                <OverlayTrigger
                                    placement="top"
                                    overlay={<StyledTooltip id={`tooltip-${index}`}>{file.name}</StyledTooltip>}
                                >
                                    <span>
                                        <SVG src={toAbsoluteUrl(decideFileType(file?.name))} />
                                    </span>
                                </OverlayTrigger>
                            </FileDetails>
                            <MaterialIcon
                                icon="close"
                                style={{ fontSize: "20px", cursor: "pointer" }}
                                onClick={() => {
                                    setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
                                }}
                            />
                        </FileItem>
                    ))}
                </Wrapper>
            ) : null}
            <StyledDragAndDrop {...getRootProps()} isDragActive={isDragActive} isDisabled={isDisabled}>
                <input {...getInputProps()} />
                <MaterialIcon icon="cloud_upload" />
                <div>
                    <div>
                        <StyledText size={size}>Drag and Drop files here or</StyledText>{" "}
                        <StyledText
                            size={size}
                            style={{
                                color: isDisabled ? colors.neutral[300] : colors.l3rnTech[900],
                                textDecoration: "underline",
                                cursor: isDisabled ? "auto" : "pointer",
                            }}
                            onClick={open}
                        >
                            browse computer
                        </StyledText>
                    </div>
                    <ExtensionSmall style={{ color: isDisabled ? colors.neutral[300] : colors.neutral[700] }}>
                        {isCvAttachment ? "File supported: PDF, DOCX" : "File supported: DOC, XLS, TXT, PDF, JPEG, PNG"}
                    </ExtensionSmall>
                </div>
            </StyledDragAndDrop>
            <Modal
                isOpen={modalOpen}
                title="Are you sure you want to delete this file?"
                variant="danger"
                icon="delete"
                successButtonText="Yes, Delete"
                cancelButtonText="Not Now"
                onClose={() => {
                    setModalOpen(false);
                    setFileId(null);
                }}
                onSuccess={() => {
                    if (isCvAttachment) {
                        handleDeleteCv();
                    } else {
                        handleDeleteAttachment(fileId);
                    }
                    setModalOpen(false);
                    setFileId(null);
                }}
            >
                <div>This action cannot be undone.</div>
            </Modal>
        </Container>
    );
}

export default ExtensionDealAttachments;
